import React, { createContext, useContext, useState, useEffect } from 'react';
import { courseModules } from '../data/courseData';

const ProgressContext = createContext();

const STORAGE_KEY = 'digital_safety_progress';

const initialProgress = {
  completedModules: [],
  quizResults: {},
  checklistItems: {},
  fillInFields: {},
  currentModule: 1,
  startedAt: null,
  completedAt: null,
};

export function ProgressProvider({ children }) {
  const [progress, setProgress] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Ensure all required fields exist and are valid
        return {
          completedModules: Array.isArray(parsed.completedModules) ? parsed.completedModules : [],
          quizResults: typeof parsed.quizResults === 'object' && parsed.quizResults !== null ? parsed.quizResults : {},
          checklistItems: typeof parsed.checklistItems === 'object' && parsed.checklistItems !== null ? parsed.checklistItems : {},
          fillInFields: typeof parsed.fillInFields === 'object' && parsed.fillInFields !== null ? parsed.fillInFields : {},
          currentModule: typeof parsed.currentModule === 'number' && parsed.currentModule >= 1 ? parsed.currentModule : 1,
          startedAt: parsed.startedAt || new Date().toISOString(),
          completedAt: parsed.completedAt || null,
        };
      }
    } catch (error) {
      console.error('Error loading progress:', error);
      // Clear corrupted localStorage
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) {}
    }
    return { ...initialProgress, startedAt: new Date().toISOString() };
  });

  // Save to localStorage whenever progress changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  }, [progress]);

  const markModuleComplete = (moduleId) => {
    setProgress((prev) => {
      if (prev.completedModules.includes(moduleId)) {
        return prev;
      }
      const newCompleted = [...prev.completedModules, moduleId];
      const isAllComplete = newCompleted.length === courseModules.length;
      
      return {
        ...prev,
        completedModules: newCompleted,
        completedAt: isAllComplete && !prev.completedAt ? new Date().toISOString() : prev.completedAt,
      };
    });
  };

  const saveQuizResult = (moduleId, passed, selectedAnswer) => {
    console.log('Saving quiz result:', { moduleId, passed, selectedAnswer });
    setProgress((prev) => {
      const newProgress = {
        ...prev,
        quizResults: {
          ...prev.quizResults,
          [moduleId]: { passed, selectedAnswer, completedAt: new Date().toISOString() },
        },
      };
      console.log('New progress state:', newProgress);
      return newProgress;
    });
  };

  const toggleChecklistItem = (moduleId, itemId) => {
    setProgress((prev) => {
      const moduleChecklist = prev.checklistItems[moduleId] || {};
      return {
        ...prev,
        checklistItems: {
          ...prev.checklistItems,
          [moduleId]: {
            ...moduleChecklist,
            [itemId]: !moduleChecklist[itemId],
          },
        },
      };
    });
  };

  const saveFillInField = (moduleId, fieldId, value) => {
    setProgress((prev) => ({
      ...prev,
      fillInFields: {
        ...prev.fillInFields,
        [moduleId]: {
          ...(prev.fillInFields[moduleId] || {}),
          [fieldId]: value,
        },
      },
    }));
  };

  const setCurrentModule = (moduleId) => {
    setProgress((prev) => {
      if (prev.currentModule === moduleId) {
        return prev;
      }
      return {
        ...prev,
        currentModule: moduleId,
      };
    });
  };

  const getModuleProgress = (moduleId) => {
    const module = courseModules.find((m) => m.id === moduleId);
    if (!module) return 0;

    let total = 1;
    let completed = 0;

    if (progress.quizResults[moduleId]?.passed) {
      completed += 1;
    }

    if (module.checklist) {
      total += module.checklist.length;
      const moduleChecklist = progress.checklistItems[moduleId] || {};
      completed += module.checklist.filter((item) => moduleChecklist[item.id]).length;
    }

    if (module.fillInFields) {
      total += module.fillInFields.length;
      const moduleFields = progress.fillInFields[moduleId] || {};
      completed += module.fillInFields.filter((field) => moduleFields[field.id]?.trim()).length;
    }

    return Math.round((completed / total) * 100);
  };

  const getOverallProgress = () => {
    const totalModules = courseModules.length;
    const completedCount = progress.completedModules.length;
    return Math.round((completedCount / totalModules) * 100);
  };

  const resetProgress = () => {
    setProgress({ ...initialProgress, startedAt: new Date().toISOString() });
  };

  const isModuleComplete = (moduleId) => {
    return progress.completedModules.includes(moduleId);
  };

  const isQuizPassed = (moduleId) => {
    return progress.quizResults[moduleId]?.passed === true;
  };

  return (
    <ProgressContext.Provider value={{
      progress,
      markModuleComplete,
      saveQuizResult,
      toggleChecklistItem,
      saveFillInField,
      setCurrentModule,
      getModuleProgress,
      getOverallProgress,
      resetProgress,
      isModuleComplete,
      isQuizPassed,
    }}>
      {children}
    </ProgressContext.Provider>
  );
}

export function useProgress() {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
}
